/**
 * The web `Platform` (see @hf/client-core `platform.ts`): localStorage, `document` visibility, the Blob-worker timer
 * scheduler, `navigator.vibrate`, Vite's `import.meta.env`. Installed by main.tsx before boot (and by the Vitest
 * setup file, so web tests see the browser behaviour they always did).
 */
import { createBrowserIntervalScheduler, documentVisibility } from './loop/browser-scheduler';
import type { KeyValueStorage, Platform, PlatformConfig } from '@hf/client-core/platform';

const viteEnv = (): Record<string, unknown> | undefined => {
  try {
    return (import.meta as unknown as { env?: Record<string, unknown> }).env;
  } catch {
    return undefined;
  }
};

export const browserConfig = (): PlatformConfig => {
  const env = viteEnv();
  const id = env?.['VITE_BUILD_ID'];
  const base = env?.['BASE_URL'];
  return {
    dev: env?.['DEV'] === true,
    mode: typeof env?.['MODE'] === 'string' ? env['MODE'] : 'development',
    buildId: typeof id === 'string' && id.length > 0 ? id : null,
    baseUrl: typeof base === 'string' ? base : '/',
  };
};

const storage = (): KeyValueStorage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

export const browserPlatform = (): Platform => {
  const config = browserConfig();
  return {
    storage,
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    visibility: documentVisibility,
    createScheduler: () => createBrowserIntervalScheduler(),
    haptics: {
      pulse(_strength, durationMs) {
        if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
        try {
          navigator.vibrate(durationMs);
        } catch {
          /* ignore */
        }
      },
    },
    log: console,
    fontsReady: () =>
      typeof document !== 'undefined' && 'fonts' in document
        ? document.fonts.ready.then(() => undefined)
        : null,
    assetUrl: (path) => config.baseUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, ''),
    viewport: () =>
      typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : { w: 1280, h: 720 },
    config,
  };
};
